import {
  IMAGE_QUERY_FIELDS,
  COMPONENT_MEDIA_QUERY,
  COMPONENT_BUTTON_QUERY,
  COMPONENT_LINK_QUERY,
  COMPONENT_TEXT_WITH_ICON_QUERY,
  COMPONENT_BLOG_CATEGORY_QUERY,
  COMPONENT_BLOG_ARTICLE_QUERY,
  COMPONENT_SOCIAL_LINK_QUERY,
  RICH_TEXT_WITH_ASSETS_FIELDS,
  COMPONENT_SINGLE_PROMISE_QUERY,
  COMPONENT_NAVBAR_QUERY,
  COMPONENT_CTA_STEP_QUERY,
  PRODUCT_SPEC_QUERY_FIELDS,
  COLLABORATOR_QUERY_FIELDS,
  COMPONENT_TITLE_TEXT_QUERY,
} from './shared';
import { getShopifyDataFieldName } from '../../shopify';
import { reformatContentfulLocaleToLocale } from '../../utils/helpers';

export const SECTION_CTA_QUERY = `
  ... on SectionCta {
    anchorId
    __typename
    sys {
      id
    }
    name
    headline
    subtitle
    textStyle
    textPosition
    textPositionMobile
    textColor
    fontFamily
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    body {
      json
    }
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
  }
`;

export const SECTION_QUIZ_QUERY = `
  ... on SectionQuiz {
    anchorId
    __typename
    sys {
      id
    }
    name
    quizId
    quizType
    openQuizOnLoad
    headline
    titleFont
    subtitle
    textStyle
    textPosition
    textPositionMobile
    textColor
    quizHeightMobile
    quizHeightDesktop
    quizWidthMobile
    quizWidthDesktop
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    body {
      json
    }
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
  }
`;

export const SECTION_PROGRAM_QUERY = `
... on SectionProgramDescription {
  anchorId
  __typename
  sys {
    id
  }
  name
  title
  titleFont
  subtitle
  image {
    ${IMAGE_QUERY_FIELDS}
  }
  media{${COMPONENT_MEDIA_QUERY}}
  description {
    json
  }
  backgroundColor {
    color
  }
  button {${COMPONENT_BUTTON_QUERY}}
}
`;

export const SECTION_RECOMMENDATION_QUERY = `
... on SectionRecommendation {
  anchorId
  __typename
  sys {
    id
  }
  name
  quote {
    json
  }
  image {
    ${IMAGE_QUERY_FIELDS}
  }
  author {
    json
  }
  role {
    json
  }
}
`;

export const SECTION_VIDEO_QUERY = `
... on SectionVideoPlayer {
  anchorId
  __typename
  sys {
    id
  }
  name
  autoplay
  video {
    ${COMPONENT_MEDIA_QUERY}
  }
}
`;

export const SECTION_IMAGE_QUERY = `
... on SectionImage {
  anchorId
  __typename
  sys {
    id
  }
  name
  media {
    ${COMPONENT_MEDIA_QUERY}
  }
}
`;

export const SECTION_TAGLINE_QUERY = `
... on SectionTagline {
  anchorId
  __typename
  sys {
    id
  }
  title
  titleFont
  tagline {
    json
  }
  button {${COMPONENT_BUTTON_QUERY}}
}
`;

export const SECTION_HERO_CAROUSEL_QUERY = `
  ... on SectionHeroCarousel {
    anchorId
    __typename
    sys {id}
    name
    slidesCollection(limit: 5){
      __typename
      items {
        ${SECTION_CTA_QUERY}
      }
    }
  }
`;

export const getSectionHighlightsQuery = locale => `
  ... on SectionHighlights {
    anchorId
    __typename
    sys {
      id
    }
    title
    highlightsCollection (limit: 15) {
      items {
            __typename
            ... on PageProduct {
          sys {
            id
          }
          title
          availableLocales
          thumbnailImage {
            ${IMAGE_QUERY_FIELDS}
          }
          shopifyData: ${getShopifyDataFieldName(reformatContentfulLocaleToLocale(locale))} 
          productPicturesCollection (limit: 1, skip: 1) {
              items {
                ${IMAGE_QUERY_FIELDS}
              }
          }
          productSpecsCollection (limit: 15) {
            items {
              ${PRODUCT_SPEC_QUERY_FIELDS}
            }
          }
          slug
          size
          productAnimationText
        }
        ... on PageCollection {
          sys {
            id
          }
          name
          title
          slug
          headlineBody {
            json
          }
          imageMobile {
            ${IMAGE_QUERY_FIELDS}
          }
          imageDesktop {
            ${IMAGE_QUERY_FIELDS}
          }
        }
        ... on PageGeneral {
          sys {
            id
          }
          name
          slug
          highlightsImage { 
            ${IMAGE_QUERY_FIELDS}
          }
        }
        ... on PageProductBundle {
          sys {
            id
          }
          title
          slug
          bundleSlogan
          productAnimationText
          availableLocales
          size
          shopifyData: ${getShopifyDataFieldName(reformatContentfulLocaleToLocale(locale))}
          productPicturesCollection (limit: 1, skip: 1) {
            items {
              ${IMAGE_QUERY_FIELDS}
            }
          }
          thumbnailImage {
            ${IMAGE_QUERY_FIELDS}
          }
        }
      }
    }
    linkForMore {
        ${COMPONENT_LINK_QUERY}
    }
  }
`;

export const SECTION_INSTAGRAM_FEED_QUERY = `
  ... on SectionInstagramFeed {
    anchorId
    __typename
    sys {
      id
    }
    title
    name
    username
    instagramPostsCollection (limit: 10) {
      items {
        ${IMAGE_QUERY_FIELDS}
      }
    }
  }
`;

export const SECTION_SIDE_IMAGE_CTA_QUERY = `
  ... on SectionSideImageCta {
    anchorId
    __typename
    sys {
      id
    }
    title
    titleFont
    name
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    body {
      json
    }
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
    style
    backgroundColor {
      color
    }
  }
`;

export const SECTION_SIDE_IMAGE_DESCRIPTION_QUERY = `
  ... on SectionSideImageDescription {
    anchorId
    __typename
    sys {
      id
    }
    title
    titleFont
    name
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    body {
      json
    }
    bodyButton {
      ${COMPONENT_BUTTON_QUERY}
    }
    style
  }
`;

export const SECTION_ICON_COLLECTION_QUERY = `
  ... on SectionIconCollection {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    iconsCollection(limit: 9) {
      items {
        ${IMAGE_QUERY_FIELDS}
      }
    }
    redirectLink {
      ... on PageGeneral {
        __typename
        slug
      }
      ... on PageProduct {
          __typename
          slug
      }
      ... on PageCollection {
          __typename
          slug
      }
    }
  }
`;

export const SECTION_BUYING_PROS_QUERY = `
  ... on SectionBuyingPros {
    anchorId
    __typename
    sys {
      id
    }
    name
    prosAndIconsCollection (limit: 5) {
      items {
        ${COMPONENT_TEXT_WITH_ICON_QUERY}
      }
    }
  }
`;

export const SECTION_TUTORIAL_QUERY = `
  ... on SectionTutorial {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    toggleNumbers
    stepsCollection {
      items {
        ${COMPONENT_TITLE_TEXT_QUERY}
        labelTutorialSection
      }
    }
    image {
      ${IMAGE_QUERY_FIELDS}
    }
  }
`;

export const SECTION_RELATED_PRODUCTS_QUERY = `
  ... on SectionRelatedProducts {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    relatedProductsCollection (limit: 4) {
      items {
        ... on PageProduct {
          __typename
          title
          slug
          availableLocales
          thumbnailImage {
            ${IMAGE_QUERY_FIELDS}
          }
          shopifyData
          productTags
          size
          productAnimationText
        }
        ... on PageProductBundle {
          __typename
          title
          slug
          bundleSlogan
          productTags
          productAnimationText
          availableLocales
          size
          shopifyData
          thumbnailImage {
            ${IMAGE_QUERY_FIELDS}
          }
        }
      }
    }
  }
`;

export const getSectionRelatedProductsQuery = locale => `
  ... on SectionRelatedProducts {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    relatedProductsCollection (limit: 10) {
      items {
        ... on PageProduct {
          __typename
          title
          slug
          availableLocales
          thumbnailImage {
            ${IMAGE_QUERY_FIELDS}
          }
          shopifyData: ${getShopifyDataFieldName(reformatContentfulLocaleToLocale(locale))}
          productTags
          size
          productAnimationText
        }
        ... on PageProductBundle {
          __typename
          title
          slug
          bundleSlogan
          productTags
          productAnimationText
          availableLocales
          size
          shopifyData: ${getShopifyDataFieldName(reformatContentfulLocaleToLocale(locale))}
          thumbnailImage {
            ${IMAGE_QUERY_FIELDS}
          }
        }
      }
    }
  }
`;

export const SECTION_TEXT_AND_SUBTITLE_QUERY = `
  ... on SectionTextAndSubtitle {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    subtitle
  }
`;

export const SECTION_BLOG_QUERY = `
  ... on SectionBlog {
    anchorId
    __typename
    sys {
      id
    }
    name
    categoriesCollection (limit: 5) {
      items {
        ${COMPONENT_BLOG_CATEGORY_QUERY} 
      }
    }
    allTopicsText
    blogArticlesCollection (limit: 120) {
      items {
        ${COMPONENT_BLOG_ARTICLE_QUERY}
      }
    }
  }
`;

export const SECTION_BLOG_ARTICLE_QUERY = `
  ... on SectionBlogArticle {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    category
    collaborator {
      ${COLLABORATOR_QUERY_FIELDS}
    }
    socialsCollection (limit: 5) {
      items {
        ${COMPONENT_SOCIAL_LINK_QUERY}
      }
    }
    headlineImage {
      ${COMPONENT_MEDIA_QUERY}
    }
    blogBody {
      ${RICH_TEXT_WITH_ASSETS_FIELDS}
    }
  }
`;

export const SECTION_READ_MORE_ARTICLES_QUERY = `
  ... on SectionReadMoreArticles {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    articleHighlightsCollection (limit: 3) {
      items {
        ${COMPONENT_BLOG_ARTICLE_QUERY}
      }
    }
  }
`;

export const SECTION_TITLE_AND_TEXT_QUERY = `
... on SectionTitleAndText{
  anchorId
  __typename
  sys{id}
  name
  title
  titleFont
  underlyingText{json}
  button {${COMPONENT_BUTTON_QUERY}}
}
`;

export const SECTION_PROMISES_QUERY = `
  ... on SectionPromises {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    subtext {
      json
    }
    promisesCollection(limit: 10){
      items {
        ${COMPONENT_SINGLE_PROMISE_QUERY}
      }
    }
  }
`;

export const SECTION_DOUBLE_CTA_QUERY = `
  ... on SectionDoubleCta {
    anchorId
    __typename
    sys {
      id
    }
    name
    ctasCollection(limit: 2){
      items {
        ${SECTION_CTA_QUERY}
      }
    }
    align
  }
`;

export const SECTION_PROFESSIONALS_HEADER_QUERY = `
  ... on SectionProfessionalsHeader {
    anchorId
    __typename
    sys {
      id
    }
    name
    navbar {
      ${COMPONENT_NAVBAR_QUERY}
    }
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
  }
`;

export const SECTION_CTA_LADDER_QUERY = `
  ... on SectionCtaLadder {
    anchorId
    __typename
    sys {
      id
    }
    name
    ctaStepsCollection (limit: 10) {
      items {
        ${COMPONENT_CTA_STEP_QUERY}
      }
    }
  }
`;

export const SECTION_BREADCRUMBS_QUERY = `
  ... on SectionBreadcrumbs {
    anchorId
    __typename
    sys {
      id
    }
    name
    breadcrumbsCollection (limit: 5) {
      items {
        ${COMPONENT_LINK_QUERY}
      }
    }
    breadcrumbEnd
    style
  }
`;

export const SECTION_BOOKEO_QUERY = `
  ... on SectionBookeo {
    anchorId
    __typename
    sys {
      id
    }
    name
  }
`;

export const SECTION_CONTACT = `
  ... on SectionContact {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    subtitle
    heroImageDesktop{
      ${IMAGE_QUERY_FIELDS}
    }
    heroImageMobile{
      ${IMAGE_QUERY_FIELDS}
    }
    infoTitle
        infoBody{
          json
        }
    consumerTitle
    consumerPhone
    consumerEmail
    salonsTitle
    salonsPhone
    salonsEmail
    addressTitle
    addressText {
      json
    }
    email
    secondaryImageDesktop{
      ${IMAGE_QUERY_FIELDS}
    }
    secondaryImageMobile{
      ${IMAGE_QUERY_FIELDS}
    }
  }
`;

export const SECTION_CONTACT_FORM = `
... on SectionContactForm {
  anchorId
  __typename
  sys {
    id
  }
    name
    title
    firstName
    lastName
    email
    phone
    orderNumber
    message
    button
    sendGridEmail
    confirmation
}
`;

export const SECTION_LOCALISED_CONTACT = `
  ... on SectionLocalisedContact {
    anchorId
    __typename
    sys {
      id
    }
    name
    contactInformationCollection (limit: 35) {
        items {
        locale
          ${SECTION_CONTACT}
        }
    }
    continentsList
    title
    subtitle
  }
`;

export const getSectionProductGridQuery = locale => `
... on SectionProductGrid {
  anchorId
  __typename
  sys {id}
  title
  link {
    ${COMPONENT_LINK_QUERY}
  }
  productsCollection (limit: 36, where: { availableLocales_contains_some: "${locale}" }) {
    items {
      ... on PageProduct {
        __typename
        sys {
          id
        }
        title
        slug
        productAnimationText
        availableLocales
        shopifyData: ${getShopifyDataFieldName(reformatContentfulLocaleToLocale(locale))} 
        size
        thumbnailImage {
          ${IMAGE_QUERY_FIELDS}
        }
      }
      ... on PageProductBundle {
        __typename
        sys {
          id
        }
        title
        slug
        productTags
        productAnimationText
        availableLocales
        bundleSlogan
        size
        shopifyData: ${getShopifyDataFieldName(reformatContentfulLocaleToLocale(locale))}
        productSpecsCollection(limit: 10) {
          items {
            ${PRODUCT_SPEC_QUERY_FIELDS}
          }
        }
        thumbnailImage {
          ${IMAGE_QUERY_FIELDS}
        }
      }
    }
  }
  imagesInBetweenCollection(limit: 3) {
    items {
      ${COMPONENT_MEDIA_QUERY}
    }
  }
}
`;

export const SECTION_KLAVIYO = `
  ... on SectionKlaviyo {
    anchorId
    __typename
    sys {
      id
    }
    name
    formId
  }
`;

export const SECTION_DOWNLOAD_PDF = `
    ... on SectionDownloadPdf {
      anchorId
      __typename
      sys {
        id
      }
      linkText
      pdfFile {
        url
      }
    }
`;

export const SECTION_ARTICLE_SLIDER_QUERY = `
  ... on SectionArticleSlider {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    blogArticlesCollection(limit: 10) {
      items {
        ${COMPONENT_BLOG_ARTICLE_QUERY}
      }
    }
  }
`;

export const SECTION_ADVICE_QUERY = `
  ... on SectionAdvice {
    anchorId
    __typename
    sys {
      id
    }
    name
    adviceArticlesCollection(limit: 20) {
      items {
      ... on ComponentAdviceArticle {
        __typename
        sys {
          id
        }
        name
        title
        titleFont
        subtitle
        headlineImage {
          ${COMPONENT_MEDIA_QUERY}
        }
        articleLink {
          text
          link {
            ... on PageGeneral {
              __typename
              slug
              name
            }
            ... on PageProduct {
              __typename
              slug
              name
            }
          }
          externalLink
        }
      }
      }
    }
  }
`;

export const SECTION_IFRAME_QUERY = `
  ... on SectionIframe {
    anchorId
    __typename
    sys {
      id
    }
    name
    url
    height
    mobileHeight
    width
    mobileWidth
  }
`;

export const SECTION_VIDEOS_QUERY = `
  ... on SectionVideos {
    anchorId
    __typename
    sys {
      id
    }
    name
    videosCollection(limit: 4) {
      items{
        title
        text
        media {
          ${COMPONENT_MEDIA_QUERY}
        }
      }
    }
  }
`;

export const SECTION_COMPONENT_CONTAINER_QUERY = `
  ... on SectionComponentContainer {
    anchorId
    __typename
    sys {
      id
    }
    name
    component {
      ...on ComponentButton {
        ${COMPONENT_BUTTON_QUERY}
      }
    }
    align
    positionTopMobile
    positionTopDesktop
    positionRightMobile
    positionRightDesktop
  }
`;

export const SECTION_GALLERY_SLIDER_QUERY = `
  ... on SectionGallerySlider {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    subtitle
    galleryCollection(limit: 8) {
      items{
        title
        text{
          json
        }
        media {
          ${COMPONENT_MEDIA_QUERY}
        }
        button {
          ${COMPONENT_BUTTON_QUERY}
        }
      }
    }
    align
  }
`;

export const SECTION_IMAGES_BEFORE_AFTER_QUERY = `
... on SectionImagesBeforeafter {
  anchorId
  __typename
  sys {
    id
  }
  name
  title
  titleFont
  text
  componentsCollection(limit: 3) {
    items {
      ... on ComponentImageBeforeafter {
        __typename
        title
        text
        imageBefore {
          ${COMPONENT_MEDIA_QUERY}
        }
        imageAfter {
          ${COMPONENT_MEDIA_QUERY}
        }
      }
    }
  }
  labelBefore
  labelAfter
}
`;

export const SECTION_BANNERS_QUERY = `
... on SectionBanners {
  anchorId
  __typename
  sys {
    id
  }
  name
  bannersCollection(limit: 4) {
    items {
      ... on ComponentMediaTextButton {
        __typename
        text
        textColor
        media {
          ${COMPONENT_MEDIA_QUERY}
        }
        button {
          ${COMPONENT_BUTTON_QUERY}
        }
        align
      }
    }
  }
}
`;

export const SECTION_COLLABORATOR_QUERY = `
... on SectionCollaborator {
  anchorId
  __typename
  sys {
    id
  }
  name
  title
  collaborator {
    ${COLLABORATOR_QUERY_FIELDS}
  }
  backgroundColor {
    color
  }
}
`;

export const SECTION_TYPEFORM_QUERY = `
  ... on SectionTypeForm {
    anchorId
    __typename
    sys {
      id
    }
    name
    typeFormId
    formType
    buttonText
    buttonColor {
      color
    }
    buttonTextColor {
      color
    }
    heightDesktop
    heightMobile
    widthDesktop
    widthMobile
  }
`;

export const SECTION_HERO_QUERY = `
  ... on SectionHero {
    anchorId
    __typename
    sys {
      id
    }
    name
    subtitle
    headline
    titleFont
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    bodyText:body
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
    layout
    contentAlignmentHorizontal
    contentAlignmentHorizontalMobile
    contentAlignmentVertical
    contentAlignmentVerticalMobile
    textColor
    textColorMobile
  }
`;

export const SECTION_BEFORE_AFTER_QUERY = `
... on SectionBeforeafter {
  anchorId
  __typename
  sys {
    id
  }
  name
  title
  titleFont
  text
  imageBefore {
    ${COMPONENT_MEDIA_QUERY}
  }
  imageAfter {
    ${COMPONENT_MEDIA_QUERY}
  }
  delimiterOrientation
}
`;

export const SECTION_BEFORE_AFTER_DUAL_QUERY = `
... on SectionBeforeafterDual {
  anchorId
  __typename
  sys {
    id
  }
  name
  sectionsCollection(limit: 2) {
    items {
      ... on SectionBeforeafter {
        anchorId
        ${SECTION_BEFORE_AFTER_QUERY}
      }
    }
  }
}
`;

export const SECTION_TEXT_AND_MEDIA_QUERY = `
  ... on SectionTextAndMedia {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    body {
      json
    }
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    mediaPosition
    contentAlignment
  }
`;

export const SECTION_MEDIA_QUERY = `
... on SectionMedia {
  anchorId
  __typename
  sys {
    id
  }
  name
  media {
    ${COMPONENT_MEDIA_QUERY}
  }
}
`;

export const SECTION_MEDIA_QUERY_PORTRAIT = `
... on SectionMediaPortrait {
  anchorId
  __typename
  sys {
    id
  }
  name
  header
  alignment
  mediaCollection(limit: 3) {
    items {
      ${COMPONENT_MEDIA_QUERY}
    }
  }
}
`;

export const SECTION_DUAL_BANNER_QUERY = `
... on SectionDualBanner {
  anchorId
  __typename
  sys {
    id
  }
  name
  bannersCollection(limit: 2) {
    items {
      text
      textColor
      textColorMobile
      sys {
        id
      }
      media {
        ${COMPONENT_MEDIA_QUERY}
      }
      link {
        ${COMPONENT_LINK_QUERY}
      }
    }
  }
}
`;

export const SECTION_MEDIA_GRID_QUERY = `
... on SectionMediaGrid {
  anchorId
  __typename
  sys {
    id
  }
  name
  title
  mediaComponentsCollection(limit: 20) {
    items {
      text
      sys {
        id
      }
      media {
        ${COMPONENT_MEDIA_QUERY}
      }
      link {
        ${COMPONENT_LINK_QUERY}
      }
      textColor
      textColorMobile
    }
  }
}
`;

export const SECTION_SPOTLIGHT_QUERY = `
  ... on SectionSpotlight {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    body {
      json
    }
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    textColor
    textColorMobile
    contentAlignmentVerticalMobile
    contentAlignmentVerticalDesktop
    hasOverlay
    overlayOpacity
  }
`;

const productCardQuerry = locale => `
... on PageProduct {
    __typename
    title
    description {
      json
    }
    slug
    availableLocales
    productAttributes
    customLabel
    productLabel {
      label
    }
    thumbnailImage {
      ${IMAGE_QUERY_FIELDS}
    }
    productSpecsCollection (limit: 15) {
      items {
        ${PRODUCT_SPEC_QUERY_FIELDS}
      }
    }
    productVariationsCollection (limit: 5) {
      items {
        ... on PageProduct {
          __typename
          slug
          title
          description {
            json
          }
          productAttributes
          customLabel
          productLabel {
            label
          }
          thumbnailImage {
            ${IMAGE_QUERY_FIELDS}
          }
          productSpecsCollection (limit: 15) {
            items {
              ${PRODUCT_SPEC_QUERY_FIELDS}
            }
          }
          availableLocales
          shopifyData: ${getShopifyDataFieldName(reformatContentfulLocaleToLocale(locale))} 
          size
        }
      }
    }
    shopifyData: ${getShopifyDataFieldName(reformatContentfulLocaleToLocale(locale))} 
    size
  }`;

export const getSectionProductCardQuery = locale => `
  ... on SectionProductCard {
    anchorId
    __typename
    sys {
      id
    }
    name
    product {
      ${productCardQuerry(locale)}  
    }
  }
`;

export const getSectionTaggedProductsQuerry = locale => `
  ... on SectionTaggedProducts {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    body {
      json
    }
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    mediaPosition
    productTagsCollection (limit: 4) {
      items {
        sys {
          id
        } 
        name
        topMobile
        topDesktop
        leftMobile
        leftDesktop
        color
        product {
          ${productCardQuerry(locale)}
        }
      }
    }
  }
`;

export const SECTION_HIGHLIGHTED_PRODUCTS_QUERY = `
  ... on SectionHighlightedProducts {
    anchorId
    __typename
    sys {
      id
    }
    title
    categoriesCollection(limit: 10) {
      items {
        ... on ComponentProductSpecs {
          ${PRODUCT_SPEC_QUERY_FIELDS}
        }
      }
    }
  }
`;

export const getAllProductsQuerry = (batchSize, locale, preview, skip) => `
  {
    pageProductCollection(limit: ${batchSize}, where: { slug_exists: true, availableLocales_contains_some: "${locale}" }, locale: "${locale}", preview: ${preview}, skip: ${
  skip || 0
}) {
      total
      items {
        __typename
        sys {
          id
        }
        title
        slug
        shopifyData: ${getShopifyDataFieldName(reformatContentfulLocaleToLocale(locale))} 
        productAttributes
        productTags
        availableLocales
        size
        productAnimationText
        customLabel
        productLabel {
          label
        }
        productSpecsCollection (limit: 25) {
          items {
            ${PRODUCT_SPEC_QUERY_FIELDS}
          }
        }
        thumbnailImage {
          ${IMAGE_QUERY_FIELDS}
        }
      }
    }
  }
`;

export const SECTION_SALON_LOCATOR_QUERY = `
  ... on SectionSalonLocator {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    body: text {
      json
    }
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    mediaOverlayLink {
      ${COMPONENT_LINK_QUERY}
    }
  }
`;

export const SECTION_REVIEWS_QUERY = `
  ... on SectionReviews {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    reviewsCollection(limit: 10) {
      items {
        author
        date
        rating
        text {
          json
          links {
            entries {
              hyperlink {
                ... on PageProduct {
                  __typename
                  sys {
                    id
                  }
                  title
                  slug
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const SECTION_COLLECTION_CTA_QUERY = `
  ... on SectionCollectionCta {
    anchorId
    __typename
    sys {
      id
    }
    name
    title
    titleFont
    body {
      json
    }
    button {
      ${COMPONENT_BUTTON_QUERY}
    }
    buttonDesktop {
      ${COMPONENT_BUTTON_QUERY}
    }
    media {
      ${COMPONENT_MEDIA_QUERY}
    }
    mediaPosition
    backgroundColor
    textColor
  }
`;

export const SECTION_VOICEFLOW_QUERY = `
  ... on SectionVoiceflow {
    anchorId
    __typename
    sys {id}
    name
    projectId
  }
`;
